import axios, { AxiosError, AxiosInstance } from "axios";
import { useQuery } from "react-query";
import {
  DateRangeReportRequest,
  PostingSheetReportRequest,
} from "../request/ReportGenerationRequest";
import {
  PlannedVsActualsPreviewData,
  PostingSheetPreviewData,
  TimesheetPreviewData,
} from "../model/ReportPreviewModels";
import attachAuthInterceptors from "./attachAuthInterceptors";

const createPreviewClient = (): AxiosInstance => {
  // Empty baseURL = same-origin. Firebase Hosting rewrites /api/reports/**
  // to the insights Cloud Run service. Local dev sets the env var to the
  // local FastAPI URL (e.g. http://localhost:8001) to bypass the rewrite.
  const baseURL = process.env.REACT_APP_TA_INSIGHTS_SERVICE_URL || "";

  const client = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });
  attachAuthInterceptors(client);
  return client;
};

const previewClient = createPreviewClient();

const PlannedVsActualsPreviewApiService = async (
  request: DateRangeReportRequest
): Promise<PlannedVsActualsPreviewData> => {
  const { data } = await previewClient.post<PlannedVsActualsPreviewData>(
    "/api/reports/preview/planned-vs-actuals",
    request
  );
  return data;
};

const TimesheetPreviewApiService = async (
  request: DateRangeReportRequest
): Promise<TimesheetPreviewData> => {
  const { data } = await previewClient.post<TimesheetPreviewData>(
    "/api/reports/preview/timesheet",
    request
  );
  return data;
};

const PostingSheetPreviewApiService = async (
  request: PostingSheetReportRequest
): Promise<PostingSheetPreviewData> => {
  const { data } = await previewClient.post<PostingSheetPreviewData>(
    "/api/reports/preview/posting-sheet",
    request
  );
  return data;
};

// 2-minute staleTime on the three preview hooks below: previews are
// session-scoped UX (the user flips between report tabs while configuring a
// report), so we want a cache hit to be free while still picking up backend
// changes within a couple of minutes. `keepPreviousData` keeps the table on
// screen during refetch so the picker doesn't blank out on every tweak.
const PREVIEW_QUERY_OPTIONS = { staleTime: 2 * 60_000 } as const;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const usePlannedVsActualsPreviewApiService = (
  request: DateRangeReportRequest,
  enabled = true
) =>
  useQuery<PlannedVsActualsPreviewData, AxiosError>(
    [
      "PlannedVsActualsPreviewApiService",
      request.scope.kind,
      request.scope.id,
      request.date_from,
      request.date_to,
      request.shift_name,
    ],
    () => PlannedVsActualsPreviewApiService(request),
    { enabled, keepPreviousData: true, ...PREVIEW_QUERY_OPTIONS }
  );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useTimesheetPreviewApiService = (
  request: DateRangeReportRequest,
  enabled = true
) =>
  useQuery<TimesheetPreviewData, AxiosError>(
    [
      "TimesheetPreviewApiService",
      request.scope.kind,
      request.scope.id,
      request.date_from,
      request.date_to,
      request.shift_name,
    ],
    () => TimesheetPreviewApiService(request),
    { enabled, keepPreviousData: true, ...PREVIEW_QUERY_OPTIONS }
  );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const usePostingSheetPreviewApiService = (
  request: PostingSheetReportRequest,
  enabled = true
) =>
  useQuery<PostingSheetPreviewData, AxiosError>(
    [
      "PostingSheetPreviewApiService",
      request.scope.kind,
      request.scope.id,
      request.date,
      request.shift_name,
    ],
    () => PostingSheetPreviewApiService(request),
    { enabled, keepPreviousData: true, ...PREVIEW_QUERY_OPTIONS }
  );
